import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — GET /fiscal/dashboard (FiscalAnalyticsController)

   Espelha `FiscalDashboardResult` (application/queries/get-fiscal-dashboard
   .query.ts) e `EmissionTransmission` (application/ports/emission-request
   .repository.ts). O DTO da apresentação (`FiscalDashboardResponseDto`)
   declara `recentTransmissions` como `Array<Record<string, unknown>>`, mas o
   query de verdade devolve `EmissionTransmission[]` com os campos abaixo.

   Query params (FiscalDashboardQueryDto):
     - companyId?: string
     - competencia?: string   (YYYY-MM; padrão do backend: mês corrente)

   O tenant vem da sessão (AuthGuard + TenantGuard) — não há parâmetro de
   tenant no contrato.
   ─────────────────────────────────────────────────────────────────────────── */

export interface TaxBurdenLine {
  taxType: string;
  label: string;
  base: number;
  value: number;
  /** Participação percentual no total de tributos do período. */
  sharePct: number;
  documentCount: number;
}

export interface FiscalDeadline {
  id: string;
  title: string;
  description: string;
  competencia: string;
  /** ISO date. */
  dueDate: string;
  status: 'OK' | 'WARNING' | 'OVERDUE';
  daysLeft: number;
}

export interface EmissionTransmission {
  id: string;
  tenantId: string;
  fiscalDocumentId: string;
  /** Evento: EMISSAO, AUTORIZACAO, DENEGACAO, CANCELAMENTO, TRANSMISSAO... */
  evento: string;
  /** Competência no formato YYYY-MM. */
  competencia: string;
  /** ISO date (o domínio declara Date; JSON serializa para string). */
  dataHora: string;
  status: string;
  providerType: string;
  sefazState: string | null;
  authorizationProtocol: string | null;
  errorMessage: string | null;
}

export interface FiscalDashboard {
  period: { startDate: string; endDate: string; competencia: string };
  taxBurden: {
    totalTaxes: number;
    grossRevenue: number;
    effectiveRatePct: number;
    breakdown: TaxBurdenLine[];
  };
  upcomingDeadlines: FiscalDeadline[];
  recentTransmissions: EmissionTransmission[];
  auditStats: { totalCalculations: number; lastCalculationAt: string | null };
}

export interface FiscalDashboardQuery {
  competencia?: string;
}

export function useFiscalDashboard(params?: FiscalDashboardQuery) {
  return useQuery({
    queryKey: ['fiscal', 'dashboard', params] as const,
    queryFn: () =>
      apiClient.get<FiscalDashboard>('/fiscal/dashboard', {
        params: {
          ...(params?.competencia ? { competencia: params.competencia } : {}),
        },
      }),
  });
}